import React from 'react';

// Formulario de una cuenta de particular: institución (opcional) + administrador (dueño de la cuenta).
// Lo comparten el alta y la edición del admin general.

export const EMPTY_PARTICULAR_FORM = {
  institucionNombre: '',
  cuit: '',
  descripcion: '',
  telefonoInstitucion: '',
  emailInstitucion: '',
  direccionCalle: '',
  direccionNumero: '',
  ciudad: '',
  provincia: '',
  codigoPostal: '',
  nombre: '',
  apellido: '',
  email: '',
  dni: '',
  telefono: ''
};

const pick = (source, key, fallback = '') => {
  if (!source) return fallback;
  const value = source[key] ?? source[key.charAt(0).toUpperCase() + key.slice(1)];
  return value ?? fallback;
};

export const mapParticularToForm = (reg) => ({
  institucionNombre: pick(reg, 'institucionNombre'),
  cuit: pick(reg, 'cuit'),
  descripcion: pick(reg, 'descripcion'),
  telefonoInstitucion: pick(reg, 'telefonoInstitucion'),
  emailInstitucion: pick(reg, 'emailInstitucion'),
  direccionCalle: pick(reg, 'direccionCalle'),
  direccionNumero: pick(reg, 'direccionNumero'),
  ciudad: pick(reg, 'ciudad'),
  provincia: pick(reg, 'provincia'),
  codigoPostal: pick(reg, 'codigoPostal'),
  nombre: pick(reg, 'nombreUsuario') || pick(reg, 'nombre'),
  apellido: pick(reg, 'apellidoUsuario'),
  email: pick(reg, 'emailUsuario') || pick(reg, 'email'),
  dni: pick(reg, 'dni'),
  telefono: pick(reg, 'telefonoUsuario')
});

export const buildParticularPayload = (form) => ({
  institucionNombre: form.institucionNombre,
  cuit: form.cuit,
  descripcion: form.descripcion,
  telefonoInstitucion: form.telefonoInstitucion,
  emailInstitucion: form.emailInstitucion,
  direccionCalle: form.direccionCalle,
  direccionNumero: form.direccionNumero,
  ciudad: form.ciudad,
  provincia: form.provincia,
  codigoPostal: form.codigoPostal,
  nombre: form.nombre,
  apellido: form.apellido,
  email: form.email,
  dni: form.dni,
  telefono: form.telefono
});

// Devuelve el primer error de validación o '' si el formulario es válido.
export const validateParticularForm = (form, { dniRequired = true } = {}) => {
  if (!form.nombre.trim()) return 'Completa el nombre del administrador.';
  if (!form.email.trim()) return 'Completa el email del administrador.';
  if (dniRequired && !form.dni.trim()) return 'Completa el DNI del administrador.';
  if (form.dni.trim() && form.dni.trim().length < 6) return 'El DNI debe tener al menos 6 caracteres.';
  if (form.cuit.trim() && !/^\d{2}-\d{8}-\d$/.test(form.cuit.trim())) return 'El CUIT debe tener el formato 20-33455684-2.';
  if (form.emailInstitucion.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.emailInstitucion.trim())) {
    return 'El email de la institución no tiene un formato válido.';
  }
  return '';
};

export const formatCuit = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 10));
  if (digits.length > 10) parts.push(digits.slice(10, 11));
  return parts.join('-');
};

const Field = ({ label, field, form, onChange, type = 'text', placeholder, ...rest }) => (
  <div className="form-field">
    <label>{label}</label>
    <input
      type={type}
      value={form[field]}
      onChange={(event) => onChange(field, event.target.value)}
      placeholder={placeholder}
      {...rest}
    />
  </div>
);

const ParticularCuentaFields = ({ form, onChange, dniRequired = true }) => (
  <>
    <section className="particular-form-section">
      <h4 className="particular-form-section-title">
        <i className="fas fa-building" />
        <span>Institución</span>
        <small>Opcional</small>
      </h4>
      <p className="particular-form-section-help">
        Si la cuenta pertenece a una institución, la cuenta se mostrará con su nombre. Si la dejas en blanco, se usa el nombre del administrador.
      </p>

      <div className="form-grid">
        <Field label="Nombre de la institución" field="institucionNombre" form={form} onChange={onChange} placeholder="Instituto de Idiomas Norte" />
        <Field
          label="CUIT"
          field="cuit"
          form={form}
          onChange={onChange}
          placeholder="20-33455684-2"
          inputMode="numeric"
          pattern="\d{2}-\d{8}-\d"
        />
      </div>

      <div className="form-field">
        <label>Descripción</label>
        <textarea
          value={form.descripcion}
          onChange={(event) => onChange('descripcion', event.target.value)}
          placeholder="Especialidad, materias o modalidad de enseñanza"
          style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px', fontFamily: 'inherit' }}
        />
      </div>

      <div className="form-grid">
        <Field label="Teléfono" field="telefonoInstitucion" form={form} onChange={onChange} placeholder="351-555-1122" />
        <Field label="Email" field="emailInstitucion" form={form} onChange={onChange} type="email" placeholder="contacto@institucion.com" />
      </div>

      <div className="form-grid">
        <Field label="Dirección - Calle" field="direccionCalle" form={form} onChange={onChange} placeholder="Av. Colón" />
        <Field label="Dirección - Número" field="direccionNumero" form={form} onChange={onChange} placeholder="1234" />
      </div>

      <div className="form-grid">
        <Field label="Ciudad" field="ciudad" form={form} onChange={onChange} placeholder="Córdoba" />
        <Field label="Provincia" field="provincia" form={form} onChange={onChange} placeholder="Córdoba" />
        <Field label="Código postal" field="codigoPostal" form={form} onChange={onChange} placeholder="5000" />
      </div>
    </section>

    <section className="particular-form-section">
      <h4 className="particular-form-section-title">
        <i className="fas fa-user-shield" />
        <span>Administrador de la cuenta</span>
      </h4>
      <p className="particular-form-section-help">
        Es el dueño de la cuenta. Ingresa con este email y su contraseña inicial. Después puedes sumar otros administradores.
      </p>

      <div className="form-grid">
        <Field label="Nombre *" field="nombre" form={form} onChange={onChange} placeholder="Nombre" />
        <Field label="Apellido" field="apellido" form={form} onChange={onChange} placeholder="Apellido" />
      </div>

      <div className="form-grid">
        <Field label="Email *" field="email" form={form} onChange={onChange} type="email" placeholder="admin@email.com" />
        <Field label={dniRequired ? 'DNI *' : 'DNI'} field="dni" form={form} onChange={onChange} placeholder="30111222" />
      </div>

      <div className="form-grid">
        <Field label="Teléfono" field="telefono" form={form} onChange={onChange} placeholder="351-555-1122" />
      </div>
    </section>
  </>
);

export default ParticularCuentaFields;
